import type { z } from 'zod';

import { invoicesList } from '~/data/generated/endpoints/invoices/invoices';
import {
  InvoicesListDisputeStatusItem,
  InvoicesListKindItem,
  type InvoicesListParams,
  InvoicesListStatusItem,
  InvoicesListTypeItem,
  InvoicesListVariant,
} from '~/data/generated/model';
import { InvoicesListResponse } from '~/data/generated/zod/invoices/invoices.zod';
import { toEnumArray, toEnumValue } from '~/data/hooks/filterParams';
import { parseApiResponse } from '~/data/parseApiResponse';
import type {
  API_InvoiceFilterResponse,
  API_InvoiceFilterSearchParamsInput,
  InvoiceListMetadata,
  InvoiceWithTickets,
} from '~/data/schemas/invoice';
import {
  API_InvoiceFilterSearchParams,
  invoiceFormListResponseSchema,
  invoiceListResponseSchema,
} from '~/data/schemas/invoice';
import type { Equal, Expect, Loosen } from '~/data/typeAssertions';

/**
 * Map the camelCase filter opts the spending grid / hooks build onto the
 * generated snake_case query params. This ports the camel→snake conversion the
 * hand-written `LiftAPIClient.invoices.filter` did (via QueryParamsBuilder),
 * centralised so `useInvoices` and the spending CSV export share it (AMP-503).
 *
 * Date ranges use the `_start`/`_end` suffix names from the runtime
 * `CustomDateRangeWidget`, which the invoices viewset now declares to
 * drf-spectacular (via `OpenApiParameter` overrides), so the generated
 * `InvoicesListParams` carries them directly. The enum-array fields are narrowed
 * from the loose frontend strings to the generated enum members via
 * {@link toEnumArray}/{@link toEnumValue} (AMP-569).
 */
export const toListParams = (opts: API_InvoiceFilterSearchParamsInput): InvoicesListParams => {
  const safe = API_InvoiceFilterSearchParams.parse(opts);

  return {
    limit: safe.limit,
    offset: safe.offset,
    ordering: safe.ordering,
    query: safe.query,
    search: safe.search,
    number: safe.number,
    property: safe.property,
    property_name: safe.propertyName,
    cars: safe.cars,
    related_invoices: safe.relatedInvoices,
    tickets: safe.tickets,
    portfolio: safe.portfolio,
    service_provider: safe.serviceProvider,
    region: safe.region,
    type: toEnumArray(safe.type, InvoicesListTypeItem),
    kind: toEnumArray(safe.kind, InvoicesListKindItem),
    status: toEnumArray(safe.status, InvoicesListStatusItem),
    dispute_status: toEnumArray(safe.disputeStatus, InvoicesListDisputeStatusItem),
    dispute_needs_attention: safe.disputeNeedsAttention,
    // The generated rejection_category param is a nested `(Item[] | null)[]`
    // (a MultipleChoiceFilter over a ListField); a flat narrow doesn't fit its
    // shape, so leave this one cast pending a schema fix (AMP-569).
    rejection_category:
      safe.rejectionCategory as unknown as InvoicesListParams['rejection_category'],
    date_start: safe.startDate,
    date_end: safe.endDate,
    date_stamped_start: safe.startDateStamped,
    date_stamped_end: safe.endDateStamped,
    date_uploaded_start: safe.startDateUploaded,
    date_uploaded_end: safe.endDateUploaded,
    proposed_amount: safe.proposedAmount?.eq,
    proposed_amount_gt: safe.proposedAmount?.gt,
    proposed_amount_gte: safe.proposedAmount?.gte,
    proposed_amount_lt: safe.proposedAmount?.lt,
    proposed_amount_lte: safe.proposedAmount?.lte,
    reviewed_amount: safe.reviewedAmount?.eq,
    reviewed_amount_gt: safe.reviewedAmount?.gt,
    reviewed_amount_gte: safe.reviewedAmount?.gte,
    reviewed_amount_lt: safe.reviewedAmount?.lt,
    reviewed_amount_lte: safe.reviewedAmount?.lte,
    savings: safe.savings?.eq,
    savings_gt: safe.savings?.gt,
    savings_gte: safe.savings?.gte,
    savings_lt: safe.savings?.lt,
    savings_lte: safe.savings?.lte,
    variant: toEnumValue(safe.variant, InvoicesListVariant),
  };
};

/**
 * Fetch filtered invoices through the generated SDK. The list endpoint returns
 * the `{ count, metadata, results }` envelope when a `limit` is supplied, but a
 * bare `Invoice[]` when it isn't. Normalise both into `API_InvoiceFilterResponse`,
 * mirroring the old client. The generated list-item type is tightened relative
 * to the hand-written `InvoiceWithTickets` the grid still consumes, so contain
 * the ripple with a documented boundary cast here rather than retyping the
 * table.
 *
 * `variant` IS forwarded to the server (`toListParams` below) and IS set by a
 * real caller — `InvoiceOverview.tsx`'s related-invoice picker calls
 * `useInvoices({ property, variant: 'form' })`. That call happens to take the
 * bare-array branch above today (it sets no `limit`), so it never reaches the
 * envelope parse — but if it ever paginates, the row shape would be the
 * `form` variant (id/number/property/serviceProvider), not the default detail
 * shape. Dispatch on `variant` so the parse always matches what the server
 * actually returns, rather than relying on that bare-array coincidence.
 *
 * The spending CSV export (`useSpending.ts`'s `onExport`) does NOT parse
 * through either envelope schema: it explicitly sets `limit`/`offset` to
 * `undefined`, so it always takes the bare-array branch above. Only the
 * paginated grid (`useInvoices` with a `limit`, the default variant) actually
 * exercises `invoiceListResponseSchema`.
 */
export const fetchInvoicesFilter = async (
  opts: API_InvoiceFilterSearchParamsInput,
): Promise<API_InvoiceFilterResponse> => {
  const params = toListParams(opts);
  const { data } = await invoicesList(params);

  if (Array.isArray(data)) {
    return {
      results: data as unknown as InvoiceWithTickets[],
      count: data.length,
      metadata: {} as InvoiceListMetadata,
    };
  }

  // This is the derived-schema fix #1205 left as a tracked follow-up, so the
  // list is parsed again — but NEVER through the raw `InvoicesListResponse`,
  // which #1205 correctly established is worse than no parse. Its `results`
  // element is a `zod.union` and zod unions are first-match-wins over non-strict
  // objects: the rich branch can't match a list row (it needs `activeContractId`,
  // "Skipped during list serialization"), so a full row falls through to the lean
  // `variant=form` branch, parses SUCCESSFULLY, and is silently stripped to
  // `{id, number, property, serviceProvider}` — gutting the grid with no failure
  // for the report-only boundary to catch. Verified against the current schema:
  // a complete row through the raw union survives with exactly those 4 keys.
  //
  // `invoiceListResponseSchema`/`invoiceFormListResponseSchema` replace `results`
  // outright (`.omit({results}).extend(...)`), so there is no union left to
  // mis-match and nothing is stripped: the same row keeps all 27 keys and money
  // is coerced to the `number` the `InvoiceWithTickets` type already promises —
  // which the unparsed passthrough could not do (it handed the grid decimal
  // STRINGS behind a number-typed cast, the money bug tier 2 exists to prevent).
  // They also fix the nullability gap the raw union has in the other direction:
  // consultant/serviceProvider/parentInvoice/emailContext/uploadedBy are typed
  // non-null but are genuinely `None` on an unassigned invoice.
  const envelopeSchema =
    params.variant === 'form' ? invoiceFormListResponseSchema : invoiceListResponseSchema;
  const validated = parseApiResponse(envelopeSchema, data, 'Invoice');
  // Cast pinned by `_PinInvoiceListMetadata` below for the `metadata` shape.
  // The `results` shape is NOT pinned: as documented above, it's genuinely
  // one of two different row shapes depending on `variant`, which this
  // return type deliberately erases — the known, accepted gap this whole
  // comment block explains.
  // eslint-disable-next-line local/no-parse-api-response-cast
  return validated as unknown as API_InvoiceFilterResponse;
};

// AMP-620: every `metadata` field is `.optional()` in the generated
// `InvoicesListResponse` (Django fields with defaults/`blank=True` making
// drf-spectacular mark them non-required), but the endpoint always computes
// and returns all of them. `Loosen` accounts for exactly that; a genuine
// field rename/removal/retype on the metadata object still fails `tsc`.
type SchemaInvoiceListMetadata = Loosen<z.infer<typeof InvoicesListResponse>['metadata']>;
type _PinInvoiceListMetadata = Expect<Equal<SchemaInvoiceListMetadata, InvoiceListMetadata>>;
